import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close, ArrowBack } from '@mui/icons-material';

export interface DetailPanelHeaderProps {
  /**
   * Primary title text
   */
  title: string;
  /**
   * Secondary subtitle text (shown below title on mobile, beside on desktop)
   */
  subtitle?: string;
  /**
   * Background color for the header (used for severity-based coloring)
   * On mobile: colors the AppBar
   * On desktop: colors the header box
   */
  backgroundColor?: string;
  /**
   * Whether to use white text (for colored backgrounds)
   * @default true when backgroundColor is provided
   */
  useWhiteText?: boolean;
  /**
   * Callback when close/back button is clicked
   */
  onClose: () => void;
  /**
   * Optional status badge or chip to display
   */
  statusBadge?: React.ReactNode;
  /**
   * Additional chips to display (e.g., severity, category)
   */
  chips?: React.ReactNode;
  /**
   * Additional actions to render in the header
   */
  actions?: React.ReactNode;
}

/**
 * Reusable header component for detail panels and dialogs.
 * Automatically adapts between mobile (AppBar with back button) and
 * desktop (colored box with close button) layouts.
 *
 * @example
 * // Alert detail panel
 * <DetailPanelHeader
 *   title={alert.alertname}
 *   backgroundColor={getSeverityColor(alert.severity)}
 *   onClose={handleClose}
 *   chips={
 *     <>
 *       <SeverityChip severity={alert.severity} />
 *       <StatusChip status={alert.status} type="alert" />
 *     </>
 *   }
 * />
 *
 * @example
 * // Exporter detail panel
 * <DetailPanelHeader
 *   title={exporter.name}
 *   subtitle={exporter.target_host}
 *   onClose={handleClose}
 *   statusBadge={<StatusChip status={exporter.status} />}
 * />
 */
export function DetailPanelHeader({
  title,
  subtitle,
  backgroundColor,
  useWhiteText = !!backgroundColor,
  onClose,
  statusBadge,
  chips,
  actions,
}: DetailPanelHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const textColor = useWhiteText ? 'white' : 'inherit';
  const chipSx = useWhiteText
    ? { bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }
    : {};

  // Mobile: Full-width AppBar with back navigation
  if (isMobile) {
    return (
      <AppBar
        position="sticky"
        sx={{
          bgcolor: backgroundColor || 'background.paper',
          color: textColor,
        }}
        elevation={backgroundColor ? 0 : 1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>

          <Box sx={{ flex: 1, ml: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" component="div" noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, color: textColor }}
                noWrap
              >
                {subtitle}
              </Typography>
            )}
            {chips && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {React.Children.map(chips, (chip) =>
                  React.isValidElement(chip)
                    ? React.cloneElement(chip as React.ReactElement<{ sx?: object }>, {
                        sx: {
                          ...(chip.props as { sx?: object }).sx,
                          ...chipSx,
                          height: 20,
                          fontSize: '0.65rem',
                        },
                      })
                    : chip
                )}
              </Box>
            )}
          </Box>

          {statusBadge && <Box sx={{ ml: 1 }}>{statusBadge}</Box>}
          {actions}

          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ ml: 1 }}
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>
    );
  }

  // Desktop: Colored header box with close button
  return (
    <Box
      sx={{
        p: 2,
        borderBottom: backgroundColor ? 0 : 1,
        borderColor: 'divider',
        bgcolor: backgroundColor || 'background.paper',
        color: textColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {title}
            </Typography>
            {statusBadge}
          </Box>

          {subtitle && (
            <Typography
              variant="body2"
              sx={{ opacity: 0.9, color: textColor, mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}

          {chips && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {React.Children.map(chips, (chip) =>
                React.isValidElement(chip)
                  ? React.cloneElement(chip as React.ReactElement<{ sx?: object }>, {
                      sx: {
                        ...(chip.props as { sx?: object }).sx,
                        ...chipSx,
                      },
                    })
                  : chip
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actions}
          <IconButton
            onClick={onClose}
            sx={{ color: textColor }}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default DetailPanelHeader;
